import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Minus, Plus, ShieldCheck, TicketCheck, Loader2, Check, MapPin, Clock, BadgePercent, X, ExternalLink } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

function PartnerStrip({ event }) {
  const links = [
    ["BookMyShow", event.bookmyshow_url, { color: "#e63946", bg: "rgba(230,57,70,0.12)", border: "rgba(230,57,70,0.45)" }],
    ["District", event.district_url, { color: "#a78bfa", bg: "rgba(139,92,246,0.12)", border: "rgba(139,92,246,0.45)" }],
    ["Swiggy", event.swiggy_url, { color: "#fc8019", bg: "rgba(252,128,25,0.12)", border: "rgba(252,128,25,0.45)" }],
  ].filter(([, u]) => u);
  if (!links.length) return null;
  return (
    <div className="mt-12 border-t border-bone/10 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="partner-strip">
      <div className="relative border border-saffron/50 bg-saffron/5 p-5">
        <span className="absolute -top-3 left-5 bg-saffron text-ink text-[10px] font-display font-bold tracking-[0.15em] px-2.5 py-1" data-testid="save-badge">
          SAVE ~20% BOOKING DIRECT
        </span>
        <p className="font-display text-[10px] tracking-[0.25em] text-saffron uppercase mt-1">Book direct — you're in the right place</p>
        <p className="mt-2.5 text-sm text-bone flex items-center gap-2"><Check size={14} className="text-saffron shrink-0" /> Best price — platforms add ~20% in fees &amp; charges</p>
        <p className="mt-1.5 text-sm text-bone flex items-center gap-2"><Check size={14} className="text-saffron shrink-0" /> Instant ticket on WhatsApp with QR</p>
      </div>
      <div className="border border-bone/15 p-5">
        <p className="font-display text-[10px] tracking-[0.25em] text-ash uppercase">Also available on</p>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {links.map(([name, url, brand]) => (
            <a key={name} href={url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium transition-transform hover:scale-105"
              style={{ color: brand.color, background: brand.bg, border: `1px solid ${brand.border}` }}
              data-testid={`partner-${name.toLowerCase()}`}>
              {name} <ExternalLink size={12} />
            </a>
          ))}
        </div>
        <p className="text-xs text-ash mt-3">Their convenience fees &amp; charges add roughly 20% on top</p>
      </div>
    </div>
  );
}

export default function BookingFlow({ event }) {
  const tiers = (event.tickets?.length ? [...event.tickets] : [{ name: "Regular", price: event.price, admits: 1 }])
    .sort((a, b) => a.price - b.price);
  const [step, setStep] = useState("details");
  const [tierIdx, setTierIdx] = useState(Math.min(1, tiers.length - 1));
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [order, setOrder] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [promos, setPromos] = useState([]);
  const tier = tiers[tierIdx];
  const isGroup = tier.admits > 1;
  const isFree = tier.price === 0;
  const total = tier.price * qty;
  const discountRs = applied ? applied.discount / 100 : 0;
  const payTotal = applied ? applied.final / 100 : total;

  useEffect(() => { loadRazorpayScript(); }, []);

  useEffect(() => {
    api.get("/coupons/visible").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  const applyCoupon = async (code, amountRs) => {
    if (!code.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: code.trim(), amount: amountRs * 100 } });
      setApplied(data);
      if (data.discount === 0) toast.info(`${data.code} applied — no discount on a free ticket`);
      else toast.success(`${data.code} applied — ${data.label}`);
    } catch (err) {
      setApplied(null);
      toast.error(apiError(err, "Invalid coupon code"));
    }
    setCouponBusy(false);
  };

  // Re-validate applied coupon whenever the order total changes
  useEffect(() => {
    if (applied) applyCoupon(applied.code, total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierIdx, qty]);

  const verify = async (payload) => {
    setProcessing(true);
    try {
      const { data } = await api.post("/bookings/verify", payload);
      setTicket(data);
      setStep("success");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err, "Payment verification failed"));
    }
    setProcessing(false);
  };

  const openRazorpay = async (data) => {
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Could not load payment gateway"); return; }
    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      name: "The Band Sumiran",
      description: event.name,
      order_id: data.order_id,
      prefill: { name: form.name, contact: form.phone },
      theme: { color: "#D97736" },
      handler: (resp) => verify({
        booking_id: data.booking_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      }),
    });
    rzp.open();
  };

  const createOrder = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const { data } = await api.post("/bookings/create-order", {
        tier: tier.name, quantity: qty, ...form,
        show_id: event.id && event.id !== "current" ? event.id : "",
        coupon_code: applied ? applied.code : "",
      });
      setOrder(data);
      if (data.free) await verify({ booking_id: data.booking_id });
      else if (data.demo) setStep("pay");
      else await openRazorpay(data);
    } catch (err) {
      toast.error(apiError(err, "Could not create order"));
    }
    setProcessing(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (step === "success" && ticket) {
    return (
      <div className="max-w-xl mx-auto" data-testid="booking-success">
        <div className="text-center">
          <TicketCheck size={44} className="mx-auto text-saffron" strokeWidth={1.5} />
          <h2 className="mt-4 font-serif text-4xl text-bone">You're in.</h2>
          <p className="mt-2 text-sm text-ash">Confirmation sent on WhatsApp to {ticket.phone}</p>
        </div>
        <div className="mt-10 border border-saffron/40" data-testid="ticket-pass">
          <div className="p-7 border-b border-dashed border-saffron/40 flex items-center justify-between gap-4">
            <div>
              <p className="font-display text-[10px] tracking-[0.3em] text-ash uppercase">The Band Sumiran</p>
              <p className="mt-2 font-serif text-2xl text-bone leading-snug">{ticket.event_name}</p>
              <p className="text-xs text-ash mt-1.5">{ticket.event_date} · {ticket.event_time}</p>
              <p className="text-xs text-ash mt-0.5">
                {ticket.maps_url ? (
                  <a href={ticket.maps_url} target="_blank" rel="noreferrer" className="hover:text-saffron underline underline-offset-4 decoration-bone/20 transition-colors" data-testid="ticket-maps-link">{ticket.event_venue} — directions</a>
                ) : ticket.event_venue}
              </p>
              <p className="text-xs text-saffron mt-2">{ticket.tier} · admits {ticket.admits}</p>
            </div>
            <div className="shrink-0 bg-[#F4EFE6] p-2" data-testid="booking-ticket-qr">
              <QRCodeCanvas value={ticket.ticket_code} size={112} bgColor="#F4EFE6" fgColor="#070605" level="M" />
            </div>
          </div>
          <div className="p-7 text-center">
            <p className="font-display text-[10px] tracking-[0.3em] text-ash uppercase">Ticket Code</p>
            <p className="mt-1.5 font-serif text-5xl tracking-[0.15em] text-gold" data-testid="booking-ticket-code">{ticket.ticket_code}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-ash text-center">Screenshot this pass — show the QR or code at the entry gate.</p>
        <a href="/" className="mt-6 block text-center py-3.5 border border-bone/25 text-bone hover:border-saffron hover:text-saffron transition-colors" data-testid="booking-done-btn">
          Back to site
        </a>
      </div>
    );
  }

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-testid="booking-flow">
      <div className="lg:col-span-3">
        <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Step 1 — Choose a category</p>
        <div className="mt-4 space-y-2" data-testid="tier-list">
          {tiers.map((t, i) => {
            const sel = i === tierIdx;
            const pctOff = applied && applied.discount_type === "percent" && applied.discount > 0;
            const discUnit = pctOff ? Math.max(0, Math.round(t.price * (1 - applied.value / 100))) : null;
            return (
            <div key={t.name} role="button" tabIndex={0}
              onClick={() => { if (!sel) { setTierIdx(i); setQty(1); } }}
              onKeyDown={(e) => { if (e.key === "Enter" && !sel) { setTierIdx(i); setQty(1); } }}
              className={`w-full flex items-center justify-between gap-3 px-5 py-4 border text-left transition-colors cursor-pointer ${sel ? "border-saffron bg-saffron/10" : "border-bone/15 hover:border-bone/35"}`}
              data-testid={`tier-option-${i}`}>
              <span className="flex items-center gap-3">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${sel ? "border-saffron" : "border-bone/30"}`}>
                  {sel && <Check size={10} className="text-saffron" />}
                </span>
                <span>
                  <span className="text-sm text-bone block">{t.name}</span>
                  {t.admits > 1 && <span className="text-xs text-ash">admits {t.admits} people</span>}
                  {t.price === 0 && <span className="text-xs text-ash">free entry</span>}
                  {sel && applied && applied.discount > 0 && (
                    <span className="text-[10px] text-saffron tracking-wider" data-testid="tier-coupon-tag">{applied.code} applied{applied.discount_type === "fixed" ? ` — ₹${applied.value} off total` : ""}</span>
                  )}
                </span>
              </span>
              <span className="flex items-center gap-3 sm:gap-4">
                {sel && (
                  <span className="flex items-center gap-2 border border-bone/20 px-2 py-1" onClick={(e) => e.stopPropagation()} data-testid="tier-qty-stepper">
                    <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-1 text-bone hover:text-saffron transition-colors" data-testid="booking-qty-minus">
                      <Minus size={13} />
                    </button>
                    <span className="font-serif text-lg text-bone w-5 text-center" data-testid="booking-qty">{qty}</span>
                    <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} className="p-1 text-bone hover:text-saffron transition-colors" data-testid="booking-qty-plus">
                      <Plus size={13} />
                    </button>
                  </span>
                )}
                <span className="text-right">
                  {discUnit !== null && t.price > 0 && (
                    <span className="block text-xs text-ash line-through">₹{t.price.toLocaleString("en-IN")}</span>
                  )}
                  <span className="font-serif text-xl text-gold whitespace-nowrap">
                    {t.price === 0 ? "Free" : `₹${(discUnit ?? t.price).toLocaleString("en-IN")}`}
                  </span>
                </span>
              </span>
            </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-ash" data-testid="kids-note">Kids below 12 years are free — no ticket needed.</p>

        {promos.length > 0 && !applied && (
          <div className="mt-6" data-testid="coupon-chips-step1">
            <p className="text-[10px] tracking-[0.2em] text-ash uppercase mb-2">Offers — tap to apply</p>
            <div className="flex flex-wrap gap-2">
              {promos.map((p) => (
                <button type="button" key={p.code} onClick={() => applyCoupon(p.code, total)} disabled={couponBusy}
                  className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-saffron/50 text-saffron text-xs font-medium tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50"
                  data-testid={`coupon-chip-step1-${p.code}`}>
                  <BadgePercent size={13} /> {p.code} · {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 font-display text-xs tracking-[0.3em] text-saffron uppercase">Step 2 — Your details</p>
        <form id="booking-form" onSubmit={createOrder} className="mt-4 space-y-4" data-testid="booking-details-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" />
            <input required placeholder="Phone — ticket & confirmation on WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" />
          </div>
        </form>
      </div>

      <div className="lg:col-span-2 lg:sticky lg:top-24">
        <div className="border border-bone/15 bg-surface" data-testid="order-summary">
          {event.image_url && (
            <div className="aspect-video overflow-hidden border-b border-bone/10">
              <img src={fileUrl(event.image_url)} alt={event.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-7">
            <p className="font-display text-[10px] tracking-[0.3em] text-ash uppercase">Order Summary</p>
            <h3 className="mt-3 font-serif text-2xl text-bone leading-snug">{event.name}</h3>
            <p className="mt-2 flex items-center gap-2 text-xs text-ash">
              <MapPin size={12} className="text-saffron" />
              {event.maps_url ? (
                <a href={event.maps_url} target="_blank" rel="noreferrer" className="hover:text-saffron underline underline-offset-4 decoration-bone/20 transition-colors" data-testid="booking-maps-link">{event.venue}</a>
              ) : event.venue}
            </p>
            <p className="mt-1 flex items-center gap-2 text-xs text-ash"><Clock size={12} className="text-saffron" /> {event.date} · {event.time}</p>

            <div className="mt-6 pt-5 border-t border-bone/10" data-testid="price-box">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-bone">{tier.name} × {qty}</p>
                  <p className="text-xs text-ash mt-0.5">{isGroup ? `admits ${tier.admits} people each` : "per person"}</p>
                </div>
                <span className="font-serif text-2xl text-gold whitespace-nowrap" data-testid="booking-line-total">{total === 0 ? "Free" : `₹${total.toLocaleString("en-IN")}`}</span>
              </div>
            </div>
            {!isFree && step === "details" && (
              <div className="mt-5 pt-5 border-t border-bone/10" data-testid="coupon-box">
                {applied ? (
                  <div className="flex items-center justify-between gap-3 border border-saffron/40 bg-saffron/5 px-4 py-3" data-testid="coupon-applied">
                    <span className="flex items-center gap-2 text-sm text-saffron">
                      <BadgePercent size={15} />
                      {applied.code} — {applied.label}
                    </span>
                    <button type="button" onClick={() => { setApplied(null); setCouponInput(""); }}
                      className="text-ash hover:text-red-400 transition-colors" data-testid="coupon-remove-btn">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <>
                    {promos.length > 0 && (
                      <div className="mb-3" data-testid="coupon-chips">
                        <p className="text-[10px] tracking-[0.2em] text-ash uppercase mb-2">Offers — tap to apply</p>
                        <div className="flex flex-wrap gap-2">
                          {promos.map((p) => (
                            <button type="button" key={p.code} onClick={() => applyCoupon(p.code, total)} disabled={couponBusy}
                              className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-saffron/50 text-saffron text-xs font-medium tracking-wide hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50"
                              data-testid={`coupon-chip-${p.code}`}>
                              <BadgePercent size={13} /> {p.code} · {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="Have another code?"
                        className="flex-1 bg-ink border border-bone/15 px-4 py-3 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors tracking-widest text-sm"
                        data-testid="coupon-input" />
                      <button type="button" onClick={() => applyCoupon(couponInput, total)} disabled={couponBusy || !couponInput.trim()}
                        className="px-5 py-3 border border-saffron/60 text-saffron text-xs font-bold tracking-[0.15em] hover:bg-saffron hover:text-ink transition-colors disabled:opacity-50"
                        data-testid="coupon-apply-btn">
                        {couponBusy ? <Loader2 size={15} className="animate-spin" /> : "APPLY"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-bone/10 space-y-2">
              {applied && applied.discount > 0 && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ash">Subtotal</span>
                    <span className="text-bone/70" data-testid="booking-subtotal">₹{total.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-saffron">Coupon {applied.code}</span>
                    <span className="text-saffron" data-testid="booking-discount">− ₹{discountRs.toLocaleString("en-IN")}</span>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm text-ash">Total · admits {tier.admits * qty}</span>
                <span className="font-serif text-3xl text-gold" data-testid="booking-total">
                  {payTotal === 0 ? "Free" : `₹${payTotal.toLocaleString("en-IN")}`}
                </span>
              </div>
            </div>

            {step === "details" && (
              <>
                <button type="submit" form="booking-form" disabled={processing}
                  className="mt-7 w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="booking-proceed-btn">
                  {processing ? <Loader2 size={18} className="animate-spin" /> : null}
                  {processing ? "Creating order…" : payTotal === 0 ? "Get Free Pass" : `Proceed to Pay ₹${payTotal.toLocaleString("en-IN")}`}
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-xs text-ash">
                  <ShieldCheck size={14} className="text-saffron" /> Secured by Razorpay
                </p>
              </>
            )}

            {step === "pay" && order && (
              <div className="mt-7" data-testid="booking-demo-pay">
                <p className="text-xs text-saffron/80">Demo checkout — no real money will be charged.</p>
                <button onClick={() => verify({ booking_id: order.booking_id })} disabled={processing}
                  className="mt-4 w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="booking-pay-btn">
                  {processing ? <Loader2 size={18} className="animate-spin" /> : null}
                  {processing ? "Processing…" : `Pay ₹${(order.amount / 100).toLocaleString("en-IN")}`}
                </button>
                <button onClick={() => setStep("details")} className="mt-3 w-full py-2.5 text-sm text-ash hover:text-bone transition-colors" data-testid="booking-back-btn">
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    <PartnerStrip event={event} />
    </>
  );
}
